import AiWriteButton from "../../components/AiWriteButton";
import React, { useState, useEffect } from "react";
import { T } from "../Translate";
import { isValidPhone } from "../../utils/contactLinks";
import { useNavigate } from "react-router-dom";
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  Alert,
  FormHelperText,
} from "@mui/material";
import { AddPhotoAlternate as AddPhotoAlternateIcon } from "../../icons";
import { auth, db, uploadImage } from "../../firebase";
import { uploadButtonSx, uploadProgressSx } from "../mediaFormStyles";
import SortableImageGrid from "../../components/SortableImageGrid";
import {
  doc,
  getDoc,
  addDoc,
  collection,
  serverTimestamp,
} from "firebase/firestore";
import Atras from "../BackButton";
import { InputAdornment } from "@mui/material";
import { useSubscriptionContext } from "../SubscriptionContext";
import { getDatabase, ref, onValue } from "firebase/database";
import { barColors } from "../../theme";
import { pageContentSx } from "../../pageLayout";
import LocationAutocompleteField from "../../components/LocationAutocompleteField";

const CAR_FORM_VALIDATION = {
  whatsapp: {
    validate: (value) =>
      value && !isValidPhone(value) ? <T>Ingresá un número válido con código de país (ej: +5491122334455)</T> : "",
  },
  title: {
    required: true,
    minLength: 5,
    maxLength: 100,
    validate: (value) => {
      if (!value) return <T>El título es obligatorio</T>;
      if (value.length < 5) return <T>El título debe tener al menos 5 caracteres</T>;
      if (value.length > 100)
        return <T>El título no puede exceder 100 caracteres</T>;
      return "";
    },
  },
  description: {
    required: true,
    minLength: 20,
    maxLength: 1000,
    validate: (value) => {
      if (!value) return <T>La descripción es obligatoria</T>;
      if (value.length < 20)
        return <T>La descripción debe tener al menos 20 caracteres</T>;
      return "";
    },
  },
  price: {
    required: true,
    validate: (value) => {
      if (!value) return <T>El precio es obligatorio</T>;
      if (isNaN(value) || value <= 0)
        return <T>El precio debe ser un número válido mayor que 0</T>;
      return "";
    },
  },
  address: {
    required: true,
    validate: (value) => {
      if (!value || !value.trim()) return <T>La dirección es obligatoria</T>;
      if (value.trim().length < 3) return <T>La dirección es demasiado corta</T>;
      return "";
    },
  },
  marca: {
    required: true,
    validate: (value) => {
      if (!value) return <T>La marca es obligatoria</T>;
      return "";
    },
  },
  modelo: {
    required: true,
    validate: (value) => {
      if (!value) return <T>El modelo es obligatorio</T>;
      if (value.length < 2) return <T>El modelo debe tener al menos 2 caracteres</T>;
      return "";
    },
  },
  año: {
    required: true,
    validate: (value) => {
      if (!value) return <T>El año es obligatorio</T>;
      const year = parseInt(value);
      const currentYear = new Date().getFullYear();
      if (isNaN(year) || year < 1900 || year > currentYear + 1)
        return <T>El año debe estar entre 1900 y {currentYear + 1}</T>;
      return "";
    },
  },
  km: {
    required: true,
    validate: (value) => {
      if (!value) return <T>El kilometraje es obligatorio</T>;
      if (isNaN(value) || value < 0)
        return <T>El kilometraje debe ser un número válido</T>;
      return "";
    },
  },
  condition: {
    required: true,
    validate: (value) => {
      if (!value) return <T>La condición es obligatoria</T>;
      const validConditions = ["Nuevo", "Usado", "Reparar"];
      if (!validConditions.includes(value))
        return <T>Seleccione una condición válida</T>;
      return "";
    },
  },
  images: {
    required: true,
    validate: (value) => {
      if (!Array.isArray(value) || value.length === 0)
        return <T>Debe subir al menos una imagen</T>;
      if (value.length > 15) return <T>No puede subir más de 10 imágenes</T>;
      return "";
    },
  },
};

const PublishCar = () => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    // "Dirección" con sugerencias; `city` se completa con la ciudad de la
    // sugerencia elegida (la usan los filtros y el orden por ciudad de cars.js).
    address: "",
    city: "",
    marca: "",
    modelo: "",
    año: "",
    km: "",
    condition: "",
    timeToDo: new Date(),
    images: [],
    whatsapp: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const navigate = useNavigate();
  const { subscription, isSubscriptionActive } = useSubscriptionContext();

  useEffect(() => {
    if (!subscription) {
      navigate('/subscription');
    }
  }, [subscription, navigate]);

  const PLAN_LIMITS_CARS = { Free: 1, Básico: 1, Profesional: 3, Premium: Infinity };
  const [carCount, setCarCount] = useState(0);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user || !subscription) return;
    const rtdb = getDatabase();
    const now = new Date();
    const monthYear = `${now.getMonth() + 1}-${now.getFullYear()}`;
    const countsRef = ref(rtdb, `publicationCounts/${user.uid}/${monthYear}`);
    const unsubscribe = onValue(countsRef, (snap) => {
      setCarCount(snap.exists() ? snap.val().cars || 0 : 0);
    });
    return () => unsubscribe();
  }, [subscription]);

  const carLimit = PLAN_LIMITS_CARS[subscription?.planName] ?? 0;
  const canPublishCar = isSubscriptionActive && carCount <= carLimit;
  const carLimitMsg = carCount > carLimit
    ? `Has superado el límite de ${carLimit} auto${carLimit !== 1 ? "s" : ""} por mes de tu plan ${subscription?.planName}. Mejora tu plan para publicar más.`
    : "";

  // Función para validar un campo específico
  const validateField = (name, value) => {
    const fieldValidation = CAR_FORM_VALIDATION[name];
    if (!fieldValidation) return "";
    return fieldValidation.validate(value);
  };

  // Validar todo el formulario
  const validateForm = () => {
    const errors = {};
    let isValid = true;

    Object.keys(CAR_FORM_VALIDATION).forEach((fieldName) => {
      const error = validateField(fieldName, formData[fieldName]);
      if (error) {
        errors[fieldName] = error;
        isValid = false;
      }

      if (!formData.price) {
        errors.price = <T>El precio es requerido</T>;
        isValid = false;
      } else {
        const price = parseFloat(formData.price);
        if (isNaN(price)) {
          errors.price = <T>Ingrese un precio válido</T>;
          isValid = false;
        } else if (price <= 0) {
          errors.price = <T>El precio debe ser mayor a 0</T>;
          isValid = false;
        } else if (price > 999999.99) {
          errors.price = <T>El precio no puede exceder 999,999.99</T>;
          isValid = false;
        }
      }
    });

    return { errors, isValid };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (submitAttempted) {
      const error = validateField(name, value);
      setFormErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);

    if (!files.length) return;

    if (formData.images.length + files.length > 10) {
      setFormErrors((prev) => ({
        ...prev,
        images: <T>No puede subir más de 10 imágenes</T>,
      }));
      return;
    }

    setLoading(true);

    try {
      const imageUrls = [];
      for (const file of files) {
        const imageUrl = await uploadImage(file);
        imageUrls.push(imageUrl);
      }

      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, ...imageUrls],
      }));

      // Validar imágenes después de la actualización
      const error = validateField("images", [...formData.images, ...imageUrls]);
      setFormErrors((prev) => ({
        ...prev,
        images: error,
      }));
    } catch (error) {
      setFormErrors((prev) => ({
        ...prev,
        images: <T>Error al subir las imágenes</T>,
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveImage = (index) => {
    const updatedImages = formData.images.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      images: updatedImages,
    }));

    if (submitAttempted) {
      const error = validateField("images", updatedImages);
      setFormErrors((prev) => ({
        ...prev,
        images: error,
      }));
    }
  };

  const handlePublishCar = async () => {
    setSubmitAttempted(true);
    const { errors, isValid } = validateForm();

    if (!isValid) {
      setFormErrors(errors);
      return;
    }

    try {
      setLoading(true);
      const { uid } = auth.currentUser || {};

      const userDocRef = doc(db, "users", uid);
      const userDocSnapshot = await getDoc(userDocRef);

      if (!userDocSnapshot.exists()) {
        throw new Error("Usuario no encontrado");
      }

      const userData = userDocSnapshot.data();
      await addDoc(collection(db, "cars"), {
        name: userData.displayName,
        ...formData,
        whatsapp: formData.whatsapp.trim(),
        address: formData.address.trim(),
        city: formData.city || formData.address.trim(),
        createdAt: serverTimestamp(),
        uid,
      });

      setPublishSuccess(true);
    } catch (error) {
      setFormErrors((prev) => ({
        ...prev,
        submit: <T>Error al publicar el vehículo: {error.message}</T>,
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleRedirectHome = () => {
    navigate("/home");
  };

  // Los estilos se mantienen igual...

  return (
    <Container maxWidth={false} disableGutters sx={pageContentSx}>
      <AppBar position="fixed" sx={{ bgcolor: barColors.background, color: barColors.text }}>
        <Toolbar>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
              maxWidth: { xs: "100%", md: "980px" },
              mx: "auto",
              px: 2,
              boxSizing: "border-box",
            }}
          >
            <Atras color="inherit" />
            <Typography variant="h5" noWrap>
              <T>Publicar Vehiculo</T>
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Container sx={{ mt: 14, mb: 20 }}>
        {formErrors.submit && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formErrors.submit}
          </Alert>
        )}

        <TextField
          autoFocus
          margin="dense"
          name="title"
          label={<T>Título</T>}
          type="text"
          fullWidth
          value={formData.title}
          onChange={handleInputChange}
          error={!!formErrors.title}
          helperText={formErrors.title}
          required
        />

        <TextField
          margin="dense"
          name="description"
          label={<T>Características del vehículo</T>}
          type="text"
          fullWidth
          multiline
          rows={5}
          value={formData.description}
          onChange={handleInputChange}
          error={!!formErrors.description}
          helperText={formErrors.description}
          required
        />
        <AiWriteButton
          type="car"
          fields={{
            titulo: formData.title,
            caracteristicas: formData.description,
            marca: formData.marca,
            modelo: formData.modelo,
            año: formData.año,
            km: formData.km,
            condicion: formData.condition,
            precio: formData.price,
            ciudad: formData.city,
          }}
          onResult={({ title, description }) =>
            setFormData((prev) => ({ ...prev, title: title || prev.title, description: description || prev.description }))
          }
        />
        <TextField
          margin="dense"
          id="price"
          label={<T>Precio en USD</T>}
          type="text"
          fullWidth
          required
          name="price"
          value={formData.price}
          onChange={(e) => {
            const value = e.target.value;

            // Solo permite números y un punto decimal
            if (/^\d*\.?\d*$/.test(value)) {
              // Si hay punto decimal, limita a 2 decimales
              if (value.includes(".")) {
                const [whole, decimal] = value.split(".");
                const formattedValue = `${whole}.${decimal.slice(0, 2)}`;
                setFormData((prev) => ({
                  ...prev,
                  price: formattedValue,
                }));
              } else {
                setFormData((prev) => ({
                  ...prev,
                  price: value,
                }));
              }
            }
          }}
          onBlur={(e) => {
            // Al perder el foco, asegura formato con 2 decimales
            const value = e.target.value;
            if (value) {
              const numberValue = parseFloat(value);
              if (!isNaN(numberValue)) {
                setFormData((prev) => ({
                  ...prev,
                  price: numberValue.toFixed(2),
                }));
              }
            }
          }}
          error={!!formErrors.price}
          helperText={formErrors.price}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
        />
        <TextField
          margin="dense"
          name="whatsapp"
          label={<T>WhatsApp de contacto (opcional)</T>}
          type="tel"
          fullWidth
          variant="outlined"
          value={formData.whatsapp || ""}
          onChange={handleInputChange}
          error={!!formErrors.whatsapp}
          helperText={formErrors.whatsapp || <T>Con código de país, ej: +5491122334455. Se muestra en la publicación con un botón de WhatsApp.</T>}
        />

        <TextField
          margin="dense"
          name="modelo"
          label={<T>Modelo</T>}
          type="text"
          fullWidth
          value={formData.modelo}
          onChange={handleInputChange}
          error={!!formErrors.modelo}
          helperText={formErrors.modelo}
          required
        />

        <TextField
          margin="dense"
          name="año"
          label={<T>Año</T>}
          type="number"
          fullWidth
          value={formData.año}
          onChange={handleInputChange}
          error={!!formErrors.año}
          helperText={formErrors.año}
          required
        />

        <TextField
          margin="dense"
          name="km"
          label={<T>Kilometraje</T>}
          type="number"
          fullWidth
          value={formData.km}
          onChange={handleInputChange}
          error={!!formErrors.km}
          helperText={formErrors.km}
          required
        />

        <Typography sx={{ mt: 2 }}><T>Marca:</T></Typography>
        <Select
          name="marca"
          value={formData.marca}
          onChange={handleInputChange}
          fullWidth
          error={!!formErrors.marca}
          required
        >
          <MenuItem value="" disabled>
            <T>Selecciona una marca</T>
          </MenuItem>
          {[
            "Acura",
            "Alfa Romeo",
            "Aston Martin",
            "Audi",
            "Bentley",
            "BMW",
            "Bugatti",
            "Buick",
            "BYD",
            "Cadillac",
            "Changan",
            "Chery",
            "Chevrolet",
            "Chrysler",
            "Citroën",
            "Dodge",
            "DS",
            "Ferrari",
            "Fiat",
            "Ford",
            "Genesis",
            "GMC",
            "Great Wall",
            "Honda",
            "Hyundai",
            "Infiniti",
            "Isuzu",
            "Jaguar",
            "Jeep",
            "Kia",
            "Lada",
            "Lamborghini",
            "Land Rover",
            "Lexus",
            "Maserati",
            "Mazda",
            "McLaren",
            "Mercedes Benz",
            "Mini",
            "Mitsubishi",
            "Nissan",
            "Opel",
            "Peugeot",
            "Porsche",
            "RAM",
            "Renault",
            "Rolls-Royce",
            "Saab",
            "Seat",
            "Škoda",
            "SsangYong",
            "Subaru",
            "Suzuki",
            "Tesla",
            "Toyota",
            "Volkswagen",
            "Volvo",
          ].map((marca) => (
            <MenuItem key={marca} value={marca}>
              {marca}
            </MenuItem>
          ))}
        </Select>
        {formErrors.marca && (
          <FormHelperText error>{formErrors.marca}</FormHelperText>
        )}

        <Typography sx={{ mt: 2 }}><T>Condición del vehículo:</T></Typography>
        <Select
          name="condition"
          value={formData.condition}
          onChange={handleInputChange}
          fullWidth
          error={!!formErrors.condition}
          required
        >
          <MenuItem value="" disabled>
            <T>Selecciona la condición</T>
          </MenuItem>
          <MenuItem value="Nuevo"><T>Nuevo</T></MenuItem>
          <MenuItem value="Usado"><T>Usado</T></MenuItem>
          <MenuItem value="Reparar"><T>Para reparar</T></MenuItem>
        </Select>
        {formErrors.condition && (
          <FormHelperText error>{formErrors.condition}</FormHelperText>
        )}

        <LocationAutocompleteField
          mode="address"
          margin="dense"
          name="address"
          label={<T>Dirección</T>}
          value={formData.address}
          onChange={(address) => handleInputChange({ target: { name: "address", value: address } })}
          onSelect={(opt) => setFormData((prev) => ({
            ...prev,
            city: opt.city || prev.city,
            latitude: opt.latitude,
            longitude: opt.longitude,
          }))}
          error={!!formErrors.address}
          helperText={formErrors.address}
          required
        />

        {/* Sección de imágenes */}
        {(formErrors?.images || formData.images.length === 0) && (
          <Alert
            severity={formErrors?.images ? "error" : "info"}
            sx={{ mt: 2, mb: 1 }}
          >
            {formErrors?.images ||
              <T>Debe subir al menos una imagen del vehículo</T>}
          </Alert>
        )}

        <label htmlFor="upload-button" style={{ width: "100%" }}>
          <Button
            variant="contained"
            component="div"
            sx={uploadButtonSx(!!formErrors?.images)}
            disabled={loading || formData.images.length >= 10}
          >
            <AddPhotoAlternateIcon />
            {formData.images.length >= 10 ? (
              <T>Límite de imágenes alcanzado</T>
            ) : formData.images.length > 0 ? (
              <T>Subir imagen ({formData.images.length}/10)</T>
            ) : (
              <T>Subir imagenes</T>
            )}
            {loading && (
              <CircularProgress size={40} thickness={4} sx={uploadProgressSx} />
            )}
          </Button>
        </label>

        <input
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          style={{ display: "none" }}
          id="upload-button"
          multiple
        />

        <SortableImageGrid
          images={formData.images}
          onChange={(images) => setFormData((prev) => ({ ...prev, images }))}
          onRemove={handleRemoveImage}
          hasError={!!formErrors?.images}
        />

        {/* Sección de éxito y botones */}
        {publishSuccess ? (
          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              <T>El vehículo se ha publicado correctamente</T>
            </Alert>
            <Button
              variant="contained"
              onClick={handleRedirectHome}
              color="primary"
              sx={{ mt: 2 }}
            >
              <T>Ir a Inicio</T>
            </Button>
          </Box>
        ) : (
          <Box sx={{ mt: 3 }}>
            {submitAttempted && Object.keys(formErrors).length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <T>Por favor, corrija los errores antes de publicar</T>
              </Alert>
            )}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="caption" color={carCount >= carLimit ? "error" : "text.secondary"}>
                <T>Autos este mes: {carCount} / {carLimit === Infinity ? "∞" : carLimit}</T>
              </Typography>
            </Box>
            {carLimitMsg && (
              <Alert severity="warning" sx={{ mb: 1 }} action={
                <Button size="small" color="inherit" onClick={() => navigate("/subscription")}><T>Ver planes</T></Button>
              }>{carLimitMsg}</Alert>
            )}
            <Button
              variant="contained"
              fullWidth
              onClick={handlePublishCar}
              color="primary"
              disabled={loading || !canPublishCar}
            >
              {loading ? <CircularProgress size={24} sx={{ mr: 1 }} /> : null}
              <T>Publicar Vehículo</T>
            </Button>
          </Box>
        )}
      </Container>
    </Container>
  );
};

export default PublishCar;
